import {
  QueryClient,
  useMutation,
  useMutationState,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { isEmpty, join, map, pipe, replace, split, toLower, trim } from 'ramda';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  firebaseDeleteApi,
  firebaseGetApi,
  firebasePatchApi,
  firebasePostApi,
} from '../../../api/firebaseApi';
import {
  toReamsBuildingAnalytics,
  trackReamsBuilding,
} from '../../../helpers/analytics';
import { features } from '../../dashboards/dashboardFramework/helpers/features';
import {
  IBuildingListProperty,
  groupPropertiesByBuildings,
} from '../helpers/buildings';
import { ILocationType, LOCATION_TYPE } from '../helpers/helper';
import { useHasBuildingGroupFeature } from '../helpers/useHasBuildingGroupFeature';
import {
  IBuilding,
  IBuildingEstimatedRent,
  IBuildingEstimatedRentsResponse,
  IBuildingMarketRentsResponse,
  IBuildingMetricsStatusResponse,
  IBuildingScenarioResponse,
  IBuildingScenariosResponse,
  IBuildingUnitsResponse,
  ICreateBuildingPayload,
  ICreateBuildingResponse,
  ICreateBuildingScenarioPayload,
  IDeleteBuildingScenarioResponse,
  IExistingAddressBuilding,
  IMoveBuildingUnitsPayload,
  IMoveBuildingUnitsResponse,
  IProperty,
  IUpdateBuildingPayload,
  IUpdateBuildingScenarioPayload,
} from '../types/types';

const BASE_URL = '/reams';

const askingPriceEndpoints = {
  priceEstimation: '/asking-prices/price-estimation',
  ownershipFinancials: '/asking-prices/ownership-financials',
  yieldBenchmarks: '/asking-prices/yield-benchmarks',
} as const;

const askingPricePredictionQueryKey = 'asking-price-prediction';
const PRICE_PROCESSING_POLL_INTERVAL_MS = 5000;
const PRICE_PROCESSING_POLL_TIMEOUT_MS = 10 * 60 * 1000;
const ownershipFinancialsQueryKey = 'ownership-financials';
const ownershipFinancialsMutateKey = 'update-ownership-financials';
const yieldBenchmarksQueryKey = 'yield-benchmarks';
const rentalCompanyNamesQueryKey = 'reams-rental-company-names';
const saveConfigurationMutateKey = 'reams-save-portfolio-configuration';

export interface IPortfolioConfig {
  excludedCompanies: string[];
  sharepointExcludedCompanies?: string[];
  excludeOwnCompany?: boolean;
}

const reamsEndPoints = {
  properties: `${BASE_URL}/properties`,
  property: `${BASE_URL}/property`,
  countryFromPoint: `${BASE_URL}/property/country`,
  rents: `${BASE_URL}/rents`,
  prediction: `${BASE_URL}/prediction`,
  trialUsageAndLimitations: `${BASE_URL}/trial-usage-limits`,
  services: `${BASE_URL}/services`,
  municipalitySummary: `${BASE_URL}/municipality-summary`,
  validateDownload: `${BASE_URL}/validate-trial-download`,
  detailedProperties: `${BASE_URL}/properties/details`,
  report: `${BASE_URL}/property/report`,
  dataSourcedDate: `${BASE_URL}/data-sources`,
  saveProperty: `${BASE_URL}/property/save`,
  validateAndReverseGeocode: `${BASE_URL}/validate-property-point`,
  rentEstimateRating: `${BASE_URL}/rent-rating`,
  saveConfiguration: `${BASE_URL}/save-portfolio-configuration`,
  portfolioConfiguration: `${BASE_URL}/portfolio-configuration`,
  recalculationStatus: `${BASE_URL}/recalculation-status`,
  buildings: `${BASE_URL}/buildings`,
  building: `${BASE_URL}/building`,
  buildingUnits: `${BASE_URL}/building/units`,
  buildingEstimatedRents: `${BASE_URL}/building/estimated-rents`,
  buildingMetricsStatus: `${BASE_URL}/building/metrics-status`,
  buildingScenarios: `${BASE_URL}/building/scenarios`,
  buildingMarketRents: `${BASE_URL}/building/market-rents`,
  buildingRegister: `${BASE_URL}/building/register`,
  moveBuildingUnits: `${BASE_URL}/building/units/move`,
};

export const reamsPropertiesQueryKey = 'reams-properties-query-key';
export const reamsPropertyQueryKey = 'reams-property-query-key';

export const rentNavigatorNewReport = 'rent-navigator-new-report';

export const reamsBuildingsQueryKey = 'reams-buildings';
export const reamsBuildingQueryKey = 'reams-building';
export const reamsBuildingUnitsQueryKey = 'reams-building-units';
export const reamsBuildingEstimatedRentsQueryKey =
  'reams-building-estimated-rents';
export const reamsBuildingMetricsStatusQueryKey =
  'reams-building-metrics-status';
export const reamsBuildingScenariosQueryKey = 'reams-building-scenarios';
const reamsBuildingMarketRentsQueryKey = 'reams-building-market-rents';
const createBuildingMutateKey = 'reams-create-building';
const updateBuildingMutateKey = 'reams-update-building';
const moveBuildingUnitsMutateKey = 'reams-move-building-units';

/** Error code of the 409 answered for moves of units still being processed. */
export const UNITS_PROCESSING_ERROR_CODE = 'UNITS_PROCESSING';

const BUILDING_UNITS_POLL_INTERVAL_MS = 5000;
const BUILDING_UNITS_POLL_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Refreshes the building list and, when a building is named (the buildingId
 * of a property response), that building's details and units.
 */
export const invalidateRentNavigatorBuilding = (
  queryClient: QueryClient,
  buildingId?: number | null,
  dataset?: string | null,
) => {
  const scope = (key: string) =>
    dataset ? [key, buildingId, dataset] : [key, buildingId];

  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: [reamsBuildingsQueryKey],
      exact: false,
    }),
    ...(buildingId
      ? [reamsBuildingQueryKey, reamsBuildingUnitsQueryKey].map((key) =>
          queryClient.invalidateQueries({ queryKey: scope(key), exact: false }),
        )
      : []),
  ]);
};

/** Error code of the 409 answered when the address already has a building. */
export const BUILDING_ADDRESS_EXISTS_ERROR_CODE = 'BUILDING_ADDRESS_EXISTS';

/** The buildings at the address when creating a building answered 409. */
export const getExistingAddressBuildings = (
  error: unknown,
): IExistingAddressBuilding[] | null => {
  const { response } = (error ?? {}) as AxiosError<{
    code?: string;
    existingBuildings?: IExistingAddressBuilding[];
  }>;

  return response?.status === 409 &&
    response?.data?.code === BUILDING_ADDRESS_EXISTS_ERROR_CODE
    ? (response.data.existingBuildings ?? [])
    : null;
};

export const fetchBuildingRegister = (
  building: { buildingId: number; dataset: string },
  signal?: AbortSignal,
) =>
  firebaseGetApi(
    reamsEndPoints.buildingRegister,
    { buildingId: building.buildingId, dataset: building.dataset },
    { signal },
  );

export const UNITS_COUNTRY_MISMATCH_ERROR_CODE = 'UNITS_COUNTRY_MISMATCH';

export const isUnitsCountryMismatchError = (error: unknown): boolean => {
  const { response } = (error ?? {}) as AxiosError<{ code?: string }>;

  return (
    response?.status === 422 &&
    response?.data?.code === UNITS_COUNTRY_MISMATCH_ERROR_CODE
  );
};

export const PROPERTY_COUNTRY_CHANGE_ERROR_CODE = 'PROPERTY_COUNTRY_CHANGE';

/** Saving a property's address in another country than its building's. */
export const isPropertyCountryChangeError = (error: unknown): boolean => {
  const { response } = (error ?? {}) as AxiosError<{ code?: string }>;

  return (
    response?.status === 422 &&
    response?.data?.code === PROPERTY_COUNTRY_CHANGE_ERROR_CODE
  );
};

export const isUnitsProcessingError = (error: unknown): boolean => {
  const { response } = (error ?? {}) as AxiosError<{ code?: string }>;

  return (
    response?.status === 409 &&
    response?.data?.code === UNITS_PROCESSING_ERROR_CODE
  );
};

export const TRIAL_LIMIT_REACHED_ERROR_CODE = 'TRIAL_LIMIT_REACHED';

export const getTrialLimitType = (error: unknown): string | null => {
  const { response } = (error ?? {}) as AxiosError<{
    code?: string;
    limitType?: string;
  }>;

  return response?.status === 429 &&
    response?.data?.code === TRIAL_LIMIT_REACHED_ERROR_CODE
    ? (response.data.limitType ?? null)
    : null;
};

export const getMoveUnitsErrorKey = (
  error: unknown,
): 'unitsProcessing' | 'differentCountry' | null => {
  if (isUnitsProcessingError(error)) return 'unitsProcessing';
  if (isUnitsCountryMismatchError(error) || isPropertyCountryChangeError(error))
    return 'differentCountry';
  return null;
};

const propertyMutateKey = 'reams-property';
const deletePropertyMutateKey = 'reams-delete-property';
const updatePropertyRentRatingMutateKey = 'rent-navigator-rating';
const rentsQueryKey = 'reams-rents';
const modelLineQueryKey = 'reams-model-line';
const servicesQueryKey = 'reams-services';
const municipalitySummary = 'reams-municipality-summary';
const trialUsageAndLimitationsKey = 'reams-trial-usage-and-limitations';
const generateReport = 'reams-generate-report';
const reamsDataSourcedDate = 'reams-data-sourced-date';

interface IReamsTrialUsage {
  activeLocations?: number;
  createLocation?: number;
  createReport?: number;
  downloadLocations?: number;
  downloadRents?: number;
  remainingDays?: number;
}

export interface IRentTrialLimitations {
  activeLocations?: number;
  createLocation?: number;
  createReport?: number;
  downloadLocations?: number;
  downloadRents?: number;
}

export interface IReamsTrialUsageAndLimitation {
  isLoading: boolean;
  trialLimitations?: IRentTrialLimitations;
  trialUsage?: IReamsTrialUsage;
}

export interface IRecalculationStatus {
  isRecalculating: boolean;
  ownLocationsOutdatedCount: number;
  sharepointLocationsOutdatedCount: number;
}

export type IAskingPriceModelPoint = {
  area: number;
  price_sqm: number;
};

export type SellVsHoldRecommendation = 'HOLD' | 'NEUTRAL' | 'SELL';
export type SellVsHoldVerdict = 'hold' | 'neutral' | 'sell';

export interface IPercentageAmount {
  value: number;
  amount: number;
}

export interface ISellVsHold {
  recommendation: SellVsHoldRecommendation;
  hurdlePct: number;
  rentalYieldPct: number | null;
  yieldVsHurdlePp: number | null;
  assumesFlatPrices: boolean;
  annualRent: number | null;
  contractRentMonthly: number | null;
  marketRentMonthly: number | null;
  salePrice: number;
  discount: IPercentageAmount;
  agencyCommission: IPercentageAmount;
  capitalGainsTax: number;
  debtRepayment: number | null;
  netCapitalReleased: number;
  capitalYieldPct: number | null;
  capitalGapPp: number | null;
  holdValue: number | null;
  verdict: SellVsHoldVerdict;
}

export interface IAskingPricePrediction {
  status?: 'done' | 'processing';
  buildingId?: number | null;
  isNewlyCalculated?: boolean;
  areaCode: string | null;
  municipality: string | null;
  countryCode: string | null;
  predictionComputedAt: string;
  prediction: {
    modelType: string;
    askingPricePerSquareMeter?: number;
    minAskingPricePerSquareMeter?: number;
    maxAskingPricePerSquareMeter?: number;
    askingPrice?: number;
    minAskingPrice?: number;
    maxAskingPrice?: number;
    timeRange: number;
    dataPoints: number | null;
    modelLine?: IAskingPriceModelPoint[];
    confidenceIntervalLines?: {
      confidenceIntervalUpperLine: IAskingPriceModelPoint[];
      confidenceIntervalLowerLine: IAskingPriceModelPoint[];
    };
  };
  statistics: {
    sampleSize?: number;
    averageAskingPricePerSquareMeter?: number;
    medianAskingPricePerSquareMeter?: number;
    minAskingPricePerSquareMeter?: number;
    maxAskingPricePerSquareMeter?: number;
    quantile25thAskingPricePerSquareMeter?: number;
    quantile75thAskingPricePerSquareMeter?: number;
  };
}

const cleanLocationNameForFileDownload = pipe(
  trim,
  split(/\s+/),
  map(toLower),
  join('-'),
  replace(/[^a-zäöü0-9\s-]/g, ''),
  replace(/-+/g, '-'),
);

export const useRentNavigatorProperties = ({
  datasets,
}: {
  datasets: ILocationType[];
}) => {
  const fetchLocations = ({ signal }: { signal: any }) =>
    firebaseGetApi(reamsEndPoints.properties, { datasets }, { signal });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [reamsPropertiesQueryKey, datasets],
    queryFn: fetchLocations,
  });

  return {
    properties: data?.properties || [],
    isInitialLoading: isLoading,
    isRefetching: isFetching,
  };
};

export const useRentNavigatorAddProperty = (
  isFirstProperty: boolean = false,
  { deferListRefresh = false }: { deferListRefresh?: boolean } = {},
) => {
  const queryClient = useQueryClient();

  const addLocationApiCall = (payload: any) =>
    firebasePostApi(reamsEndPoints.property, payload);

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [propertyMutateKey],
    mutationFn: addLocationApiCall,
    onSettled: (_data, _error, { id, dataset }) =>
      Promise.all([
        !isFirstProperty &&
          !deferListRefresh &&
          queryClient.invalidateQueries({
            queryKey: [reamsPropertiesQueryKey],
            exact: false,
          }),
        !deferListRefresh &&
          queryClient.invalidateQueries({
            queryKey: [trialUsageAndLimitationsKey],
            exact: true,
          }),
        queryClient.invalidateQueries({
          queryKey: [modelLineQueryKey, id, dataset],
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: [rentsQueryKey, id, dataset],
          exact: false,
        }),
      ]),
    onSuccess: (newProperty) => {
      queryClient.setQueryData(
        [reamsPropertyQueryKey, newProperty.id, newProperty.dataset],
        newProperty,
      );
      queryClient.setQueryData([rentNavigatorNewReport, newProperty.id], false);
      if (!deferListRefresh) {
        invalidateRentNavigatorBuilding(
          queryClient,
          newProperty?.buildingId,
          LOCATION_TYPE.ownLocation,
        );
      }

      // Editing the property changes the model's own inputs
      if (newProperty?.id) {
        queryClient.resetQueries({
          queryKey: [askingPricePredictionQueryKey, newProperty.id],
        });
        // "Current or expected rent" writes the same rent_per_month that the
        queryClient.invalidateQueries({
          queryKey: [ownershipFinancialsQueryKey, newProperty.id],
        });
      }
    },
  });

  return { addLocation: mutateAsync, isPending };
};

export const useRentNavigatorSaveProperty = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['reams-save-property'],
    mutationFn: (propertyId: number) =>
      firebasePatchApi(reamsEndPoints.saveProperty, { propertyId }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [reamsPropertiesQueryKey],
        exact: false,
      }),
  });

  return { saveProperty: mutateAsync, isSaving: isPending };
};

export const useRentNavigatorProperty = ({
  propertyId,
  dataset,
}: {
  propertyId: number | null;
  dataset: string | null;
}): {
  property: IProperty;
  isLoading: boolean;
  error: AxiosError;
  isError: boolean;
} => {
  const fetchLocation = ({ signal }: { signal: any }) =>
    firebaseGetApi(
      reamsEndPoints.property,
      { propertyId, dataset },
      { signal },
    );

  const isEnabled = Boolean(propertyId) && Boolean(dataset);

  const { data, isLoading, isFetching, error, isError } = useQuery({
    queryKey: [reamsPropertyQueryKey, propertyId, dataset],
    queryFn: fetchLocation,
    enabled: isEnabled,
  });

  return {
    property: data as IProperty,
    isLoading: isLoading || isFetching,
    error: error as AxiosError,
    isError,
  };
};

export const useRentNavigatorPropertiesDetails = (
  properties: { id: number; dataset: string }[],
) => {
  const queries = useQueries({
    queries: properties.map(({ id, dataset }) => ({
      queryKey: [reamsPropertyQueryKey, id, dataset],
      queryFn: ({ signal }: { signal: any }) =>
        firebaseGetApi(
          reamsEndPoints.property,
          { propertyId: id, dataset },
          { signal },
        ),
    })),
  });

  return {
    properties: queries.map((query) => query.data as IProperty | undefined),
    isLoading: queries.some((query) => query.isLoading),
  };
};

export const useRentEstimationRequests = (
  propertyId: number | null,
  dataset: string | null,
) => {
  const queryClient = useQueryClient();

  const queryKeys = useMemo(
    () => [
      [rentsQueryKey, propertyId, dataset],
      [modelLineQueryKey, propertyId, dataset],
    ],
    [propertyId, dataset],
  );

  const cancel = useCallback(() => {
    queryKeys.forEach((queryKey) => queryClient.cancelQueries({ queryKey }));
  }, [queryClient, queryKeys]);

  const resume = useCallback(() => {
    queryKeys.forEach((queryKey) =>
      queryClient.refetchQueries({
        queryKey,
        predicate: (query) => query.state.data === undefined,
      }),
    );
  }, [queryClient, queryKeys]);

  return { cancel, resume };
};

export const useRentNavigatorRents = (filters: Record<string, any>) => {
  const fetchFilteredRents = ({ signal }: { signal: any }) => {
    const params = {
      propertyId: filters?.propertyId,
      filters: filters || {},
      dataset: filters?.dataset,
    };

    return firebasePostApi(reamsEndPoints.rents, params, {
      signal,
    });
  };

  const isFetchEnabled = Boolean(filters?.propertyId) && !isEmpty(filters);

  const { data, isLoading } = useQuery({
    queryKey: [
      rentsQueryKey,
      filters?.propertyId,
      filters?.dataset,
      { ...filters },
    ],
    queryFn: fetchFilteredRents,
    enabled: isFetchEnabled,
    // keep the cache for 10 minutes
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return { rents: data?.rents, isLoading };
};

export const useRentNavigatorPredictionModel = ({
  propertyId,
  dataset,
}: {
  propertyId: number | null;
  dataset: string | null;
}) => {
  const fetchModelLine = ({ signal }: { signal: any }) => {
    const params = { propertyId, dataset };

    return firebaseGetApi(reamsEndPoints.prediction, params, { signal });
  };

  const isEnabled = Boolean(propertyId);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: [modelLineQueryKey, propertyId, dataset],
    queryFn: fetchModelLine,
    enabled: isEnabled,
  });

  return { data, isLoading: isLoading && isEnabled, error, isError };
};

export const useRentTrialUsageAndLimitations =
  (): IReamsTrialUsageAndLimitation => {
    const hasTrial = useSelector((state: any) =>
      state?.user?.globalTrialFeatures?.includes(features.REAMS),
    );

    const fetchTrialUsage = ({ signal }: { signal: any }) =>
      firebaseGetApi(reamsEndPoints.trialUsageAndLimitations, {}, { signal });

    const { data, isLoading, isFetching } = useQuery({
      queryKey: [trialUsageAndLimitationsKey],
      queryFn: fetchTrialUsage,
      enabled: hasTrial,
    });

    return {
      trialLimitations: data?.trialLimitations,
      trialUsage: data?.trialUsage,
      isLoading: isLoading || isFetching,
    };
  };

export const useDeleteRentNavigatorProperty = ({
  deferListRefresh = false,
}: {
  deferListRefresh?: boolean;
} = {}) => {
  const queryClient = useQueryClient();

  const { mutateAsync: deleteLocation, isPending } = useMutation({
    mutationKey: [deletePropertyMutateKey],
    mutationFn: ({ propertyId }: { propertyId: number }) =>
      firebaseDeleteApi(reamsEndPoints.property, {
        propertyId,
      }),
    onSuccess: async (response) => {
      if (deferListRefresh) return;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [reamsPropertiesQueryKey],
          exact: false,
        }),
        invalidateRentNavigatorBuilding(
          queryClient,
          response?.building_id,
          LOCATION_TYPE.ownLocation,
        ),
      ]);
    },
  });

  return { deleteLocation, isLoading: isPending };
};

export const useRefreshRentNavigatorProperties = () => {
  const queryClient = useQueryClient();

  return () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: [reamsPropertiesQueryKey],
        exact: false,
      }),
      queryClient.invalidateQueries({
        queryKey: [trialUsageAndLimitationsKey],
        exact: true,
      }),
      // deferred batches (Building view saves) refresh buildings here
      ...[
        reamsBuildingsQueryKey,
        reamsBuildingQueryKey,
        reamsBuildingUnitsQueryKey,
      ].map((key) =>
        queryClient.invalidateQueries({ queryKey: [key], exact: false }),
      ),
    ]);
};

export const useDeleteRentNavigatorBuilding = () => {
  const refreshProperties = useRefreshRentNavigatorProperties();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (
      buildingId: number,
    ): Promise<{ buildingId: number; deletedPropertyIds: number[] }> =>
      firebaseDeleteApi(reamsEndPoints.building, { buildingId }),
    onSuccess: ({ buildingId, deletedPropertyIds }) => {
      trackReamsBuilding('deleted', {
        buildingId,
        deletedUnitsCount: deletedPropertyIds.length,
      });
      return refreshProperties();
    },
  });

  return { deleteBuilding: mutateAsync, isDeleting: isPending };
};

const reamsBuildingsQueryOptions = (datasets: ILocationType[]) => ({
  queryKey: [reamsBuildingsQueryKey, datasets],
  queryFn: ({ signal }: { signal?: AbortSignal }) =>
    firebaseGetApi(reamsEndPoints.buildings, { datasets }, { signal }),
});

/** GET /reams/buildings outside a component (cached like the hook). */
export const fetchRentNavigatorBuildings = async (
  queryClient: QueryClient,
  datasets: ILocationType[],
): Promise<IBuilding[]> => {
  if (!datasets.length) return [];
  const data = await queryClient.fetchQuery(
    reamsBuildingsQueryOptions([...datasets].sort()),
  );
  return (data?.buildings as IBuilding[]) ?? [];
};

export const useRentNavigatorBuildings = ({
  datasets,
  enabled = true,
  refetchIntervalMs,
}: {
  datasets: ILocationType[];
  enabled?: boolean;
  refetchIntervalMs?: number;
}) => {
  const hasBuildingGroup = useHasBuildingGroupFeature();
  const { data, isLoading, isFetching } = useQuery({
    ...reamsBuildingsQueryOptions(datasets),
    enabled: enabled && hasBuildingGroup,
    ...(refetchIntervalMs
      ? { refetchInterval: refetchIntervalMs, staleTime: refetchIntervalMs }
      : {}),
  });

  return {
    buildings: hasBuildingGroup ? ((data?.buildings as IBuilding[]) ?? []) : [],
    isInitialLoading: isLoading,
    isRefetching: isFetching,
  };
};

/**
 * The backend buildings (grouping and aggregates) with the given list units
 * attached, in the order of the units. Datasets come from the units.
 */
export const useRentNavigatorBuildingGroups = <T extends IBuildingListProperty>(
  units: T[],
  { refetchIntervalMs }: { refetchIntervalMs?: number } = {},
) => {
  const datasets = useMemo(
    () =>
      Array.from(new Set((units || []).map((unit) => unit.dataset)))
        .filter(Boolean)
        .sort() as ILocationType[],
    [units],
  );
  const { buildings, isInitialLoading } = useRentNavigatorBuildings({
    datasets,
    enabled: datasets.length > 0,
    refetchIntervalMs,
  });
  const buildingGroups = useMemo(
    () => groupPropertiesByBuildings(buildings, units),
    [buildings, units],
  );

  return {
    buildings: buildingGroups,
    isLoading: datasets.length > 0 && isInitialLoading,
  };
};

export const useRentNavigatorBuilding = ({
  buildingId,
  dataset,
}: {
  buildingId: number | null;
  dataset: string | null;
}) => {
  const { data, isLoading, error, isError } = useQuery({
    queryKey: [reamsBuildingQueryKey, buildingId, dataset],
    queryFn: ({ signal }) =>
      firebaseGetApi(
        reamsEndPoints.building,
        { buildingId, dataset },
        { signal },
      ),
    enabled: Boolean(buildingId) && Boolean(dataset),
  });

  return {
    building: data?.building as IBuilding | undefined,
    isLoading,
    error: error as AxiosError | null,
    isError,
  };
};

/**
 * Units of one building. Polls only while the Units tab is open and the
 * response says isProcessing, pauses while the browser tab is hidden, and
 * stops after BUILDING_UNITS_POLL_TIMEOUT_MS (isPollingTimedOut; call
 * restartPolling to try again). When polling ends the building's details
 * and the building list are refreshed.
 */
export const useRentNavigatorBuildingUnits = ({
  buildingId,
  dataset,
  isUnitsTabOpen = true,
}: {
  buildingId: number | null;
  dataset: string | null;
  isUnitsTabOpen?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
  const enabled = Boolean(buildingId) && Boolean(dataset);
  const canPoll = enabled && isUnitsTabOpen && !isPollingTimedOut;

  const { data, isLoading, isFetching, error, isError, refetch } = useQuery({
    queryKey: [reamsBuildingUnitsQueryKey, buildingId, dataset],
    queryFn: ({ signal }) =>
      firebaseGetApi(
        reamsEndPoints.buildingUnits,
        { buildingId, dataset },
        { signal },
      ),
    enabled,
    refetchInterval: (query) =>
      canPoll && (query.state.data as IBuildingUnitsResponse)?.isProcessing
        ? BUILDING_UNITS_POLL_INTERVAL_MS
        : false,
    refetchIntervalInBackground: false,
  });

  const response = data as IBuildingUnitsResponse | undefined;
  const isProcessing = Boolean(response?.isProcessing);
  const isPolling = canPoll && isProcessing;

  useEffect(() => {
    setIsPollingTimedOut(false);
  }, [buildingId, dataset]);

  useEffect(() => {
    if (!isPolling) return undefined;
    const timer = setTimeout(
      () => setIsPollingTimedOut(true),
      BUILDING_UNITS_POLL_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [isPolling]);

  const polledBuilding = useRef<{
    buildingId: number | null;
    dataset: string | null;
  } | null>(null);

  useEffect(() => {
    if (isPolling) {
      polledBuilding.current = { buildingId, dataset };
      return;
    }
    const polled = polledBuilding.current;
    polledBuilding.current = null;
    if (!polled?.buildingId) return;
    queryClient.invalidateQueries({
      queryKey: [reamsBuildingQueryKey, polled.buildingId, polled.dataset],
      exact: true,
    });
    queryClient.invalidateQueries({
      queryKey: [reamsBuildingsQueryKey],
      exact: false,
    });
  }, [isPolling, buildingId, dataset, queryClient]);

  const restartPolling = useCallback(() => {
    setIsPollingTimedOut(false);
    return refetch();
  }, [refetch]);

  return {
    units: response?.units ?? [],
    isProcessing,
    pendingUnitsCount: response?.pendingUnitsCount ?? 0,
    includedUnitsCount: response?.includedUnitsCount ?? 0,
    excludedUnitsCount: response?.excludedUnitsCount ?? 0,
    isPollingTimedOut: isPollingTimedOut && isProcessing,
    restartPolling,
    isLoading,
    isFetching,
    error: error as AxiosError | null,
    isError,
  };
};

export const useBuildingEstimatedRents = ({
  buildingId,
  dataset,
  enabled = true,
}: {
  buildingId: number | null | undefined;
  dataset: string | null | undefined;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
  const isEnabled = enabled && Boolean(buildingId) && Boolean(dataset);

  const { data } = useQuery({
    queryKey: [reamsBuildingEstimatedRentsQueryKey, buildingId, dataset],
    queryFn: ({ signal }): Promise<IBuildingEstimatedRentsResponse> =>
      firebaseGetApi(
        reamsEndPoints.buildingEstimatedRents,
        { buildingId, dataset },
        { signal },
      ),
    enabled: isEnabled,
    refetchInterval: (query) =>
      !isPollingTimedOut && query.state.data?.isProcessing
        ? BUILDING_UNITS_POLL_INTERVAL_MS
        : false,
    refetchIntervalInBackground: false,
  });

  const isProcessing = isEnabled && Boolean(data?.isProcessing);
  const isPolling = isProcessing && !isPollingTimedOut;

  useEffect(() => {
    setIsPollingTimedOut(false);
  }, [buildingId, dataset]);

  useEffect(() => {
    if (!isPolling) return undefined;
    const timer = setTimeout(
      () => setIsPollingTimedOut(true),
      BUILDING_UNITS_POLL_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [isPolling]);

  const wasPolling = useRef(false);

  useEffect(() => {
    if (isPolling) {
      wasPolling.current = true;
      return;
    }
    if (!wasPolling.current) return;
    wasPolling.current = false;
    [reamsPropertiesQueryKey, reamsBuildingsQueryKey].forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key], exact: false }),
    );
    queryClient.invalidateQueries({
      queryKey: [reamsBuildingQueryKey, buildingId, dataset],
      exact: true,
    });
  }, [isPolling, buildingId, dataset, queryClient]);

  const rentsByPropertyId = useMemo(
    () =>
      new Map<number, IBuildingEstimatedRent>(
        isEnabled
          ? (data?.units ?? []).map((unit) => [unit.propertyId, unit])
          : [],
      ),
    [data, isEnabled],
  );

  return {
    rentsByPropertyId,
    isProcessing,
    isPollingTimedOut: isPollingTimedOut && isProcessing,
  };
};

export const useBuildingMetricsStatus = ({
  buildingId,
  dataset,
  enabled = true,
}: {
  buildingId: number | null | undefined;
  dataset: string | null | undefined;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [isPollingTimedOut, setIsPollingTimedOut] = useState(false);
  const isEnabled = enabled && Boolean(buildingId) && Boolean(dataset);

  const { data } = useQuery({
    queryKey: [reamsBuildingMetricsStatusQueryKey, buildingId, dataset],
    queryFn: ({ signal }): Promise<IBuildingMetricsStatusResponse> =>
      firebaseGetApi(
        reamsEndPoints.buildingMetricsStatus,
        { buildingId, dataset },
        { signal },
      ),
    enabled: isEnabled,
    refetchInterval: (query) =>
      !isPollingTimedOut && query.state.data?.isProcessing
        ? BUILDING_UNITS_POLL_INTERVAL_MS
        : false,
    refetchIntervalInBackground: false,
  });

  const isProcessing = isEnabled && Boolean(data?.isProcessing);
  const isPolling = isProcessing && !isPollingTimedOut;

  useEffect(() => {
    setIsPollingTimedOut(false);
  }, [buildingId, dataset]);

  useEffect(() => {
    if (!isPolling) return undefined;
    const timer = setTimeout(
      () => setIsPollingTimedOut(true),
      BUILDING_UNITS_POLL_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [isPolling]);

  const wasPolling = useRef(false);

  useEffect(() => {
    if (isPolling) {
      wasPolling.current = true;
      return;
    }
    if (!wasPolling.current) return;
    wasPolling.current = false;
    [reamsPropertiesQueryKey, reamsBuildingsQueryKey].forEach((key) =>
      queryClient.invalidateQueries({ queryKey: [key], exact: false }),
    );
    queryClient.invalidateQueries({
      queryKey: [reamsBuildingQueryKey, buildingId, dataset],
      exact: true,
    });
  }, [isPolling, buildingId, dataset, queryClient]);

  return {
    isValuationCalculating: isPolling && Boolean(data?.isValuationProcessing),
    isRentCalculating: isPolling && Boolean(data?.isRentProcessing),
    isPollingTimedOut: isPollingTimedOut && isProcessing,
  };
};

export const SCENARIO_LIMIT_REACHED_ERROR_CODE = 'SCENARIO_LIMIT_REACHED';
export const DEFAULT_MAX_SCENARIOS = 10;
export const DEFAULT_MAX_SCENARIO_UNIT_TYPES = 20;

export const isScenarioLimitError = (error: unknown): boolean => {
  const { response } = (error ?? {}) as AxiosError<{ code?: string }>;
  return (
    response?.status === 409 &&
    response?.data?.code === SCENARIO_LIMIT_REACHED_ERROR_CODE
  );
};

const buildingScenariosQueryKey = (buildingId: number | null | undefined) => [
  reamsBuildingScenariosQueryKey,
  buildingId,
];

const MARKET_RENTS_STALE_MS = 60 * 60 * 1000;
const MARKET_RENTS_RETRIES = 2;

export const useBuildingMarketRents = ({
  buildingId,
  enabled = true,
}: {
  buildingId: number | null | undefined;
  enabled?: boolean;
}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: [reamsBuildingMarketRentsQueryKey, buildingId],
    queryFn: ({ signal }): Promise<IBuildingMarketRentsResponse> =>
      firebaseGetApi(
        reamsEndPoints.buildingMarketRents,
        { buildingId },
        { signal },
      ),
    enabled: enabled && Boolean(buildingId),
    staleTime: MARKET_RENTS_STALE_MS,
    retry: (failureCount, error) =>
      (error as AxiosError)?.response?.status === 503 &&
      failureCount < MARKET_RENTS_RETRIES,
  });

  return {
    rentPerSqmByRooms: data?.rentPerSqmByRooms,
    isLoading,
    isError,
  };
};

export const useBuildingScenarios = ({
  buildingId,
  enabled = true,
}: {
  buildingId: number | null | undefined;
  enabled?: boolean;
}) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: buildingScenariosQueryKey(buildingId),
    queryFn: ({ signal }): Promise<IBuildingScenariosResponse> =>
      firebaseGetApi(
        reamsEndPoints.buildingScenarios,
        { buildingId },
        { signal },
      ),
    enabled: enabled && Boolean(buildingId),
  });

  return {
    scenarios: data?.scenarios,
    maxScenarios: data?.maxScenarios ?? DEFAULT_MAX_SCENARIOS,
    maxUnitTypes: data?.maxUnitTypes ?? DEFAULT_MAX_SCENARIO_UNIT_TYPES,
    isLoading,
    isError,
  };
};

export const useBuildingScenarioMutations = (
  buildingId: number | null | undefined,
) => {
  const queryClient = useQueryClient();
  const queryKey = buildingScenariosQueryKey(buildingId);

  const updateCache = (
    update: (
      scenarios: IBuildingScenarioResponse[],
    ) => IBuildingScenarioResponse[],
  ) =>
    queryClient.setQueryData<IBuildingScenariosResponse>(queryKey, (data) =>
      data ? { ...data, scenarios: update(data.scenarios) } : data,
    );

  const { mutateAsync: createScenario, isPending: isCreating } = useMutation({
    mutationFn: async (
      payload: Omit<ICreateBuildingScenarioPayload, 'buildingId'>,
    ): Promise<IBuildingScenarioResponse> =>
      (
        await firebasePostApi(reamsEndPoints.buildingScenarios, {
          ...payload,
          buildingId,
        })
      ).scenario,
    onSuccess: (scenario) => {
      trackReamsBuilding('scenario_created', {
        buildingId: scenario.buildingId,
        scenarioId: scenario.scenarioId,
        unitTypesCount: scenario.details.length,
      });
      updateCache((scenarios) => [...scenarios, scenario]);
    },
    onError: (error) => {
      if (isScenarioLimitError(error)) {
        queryClient.invalidateQueries({ queryKey, exact: true });
      }
    },
  });

  const { mutateAsync: updateScenario, isPending: isUpdating } = useMutation({
    mutationFn: async (
      payload: IUpdateBuildingScenarioPayload,
    ): Promise<IBuildingScenarioResponse> =>
      (await firebasePatchApi(reamsEndPoints.buildingScenarios, payload))
        .scenario,
    onSuccess: (scenario) =>
      updateCache((scenarios) =>
        scenarios.map((item) =>
          item.scenarioId === scenario.scenarioId ? scenario : item,
        ),
      ),
  });

  const { mutateAsync: deleteScenario, isPending: isDeleting } = useMutation({
    mutationFn: (
      scenarioId: number,
    ): Promise<IDeleteBuildingScenarioResponse> =>
      firebaseDeleteApi(reamsEndPoints.buildingScenarios, { scenarioId }),
    onSuccess: ({ scenarioId }) => {
      trackReamsBuilding('scenario_deleted', { buildingId, scenarioId });
      updateCache((scenarios) =>
        scenarios.filter((item) => item.scenarioId !== scenarioId),
      );
    },
  });

  return {
    createScenario,
    updateScenario,
    deleteScenario,
    isCreating,
    isUpdating,
    isDeleting,
  };
};

export const useCreateRentNavigatorBuilding = () => {
  const refreshProperties = useRefreshRentNavigatorProperties();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [createBuildingMutateKey],
    mutationFn: (
      payload: ICreateBuildingPayload,
    ): Promise<ICreateBuildingResponse> =>
      firebasePostApi(reamsEndPoints.building, payload),
    onSuccess: ({ building, units }, payload) => {
      trackReamsBuilding('created', {
        ...toReamsBuildingAnalytics(building),
        unitsCount: units.length,
        locationAnalysisType: payload.locationAnalysisType,
        isDuplicateAddress: Boolean(payload.allowDuplicateAddress),
      });
      return refreshProperties();
    },
  });

  return { createBuilding: mutateAsync, isCreating: isPending };
};

/** Own buildings only: SharePoint buildings follow the SharePoint file. */
export const useUpdateRentNavigatorBuilding = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [updateBuildingMutateKey],
    mutationFn: (payload: IUpdateBuildingPayload) =>
      firebasePatchApi(reamsEndPoints.building, payload),
    onSuccess: (
      response: { building: IBuilding },
      payload: IUpdateBuildingPayload,
    ) => {
      const { building } = response;
      trackReamsBuilding('updated', {
        ...toReamsBuildingAnalytics(building),
        changedFields: Object.keys(payload).filter(
          (key) => key !== 'buildingId',
        ),
      });
      queryClient.setQueryData(
        [reamsBuildingQueryKey, building.buildingId, building.dataset],
        response,
      );
      return queryClient.invalidateQueries({
        queryKey: [reamsBuildingsQueryKey],
        exact: false,
      });
    },
  });

  return { updateBuilding: mutateAsync, isUpdating: isPending };
};

/**
 * Own buildings only. Rejects with a 409 (isUnitsProcessingError) while any
 * of the units is still being processed.
 */
export const useMoveRentNavigatorBuildingUnits = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [moveBuildingUnitsMutateKey],
    mutationFn: (
      payload: IMoveBuildingUnitsPayload,
    ): Promise<IMoveBuildingUnitsResponse> =>
      firebasePostApi(reamsEndPoints.moveBuildingUnits, payload),
    onSuccess: (
      {
        targetBuildingId,
        targetBuildingIds,
        sourceBuildingIds,
        deletedBuildingIds,
      },
      payload,
    ) => {
      trackReamsBuilding('units_moved', {
        unitsCount: payload.propertyIds.length,
        targetBuildingId: targetBuildingId ?? undefined,
        toAddressBuilding: Boolean(payload.toAddressBuilding),
        deletedBuildingsCount: deletedBuildingIds.length,
      });
      return Promise.all(
        [...targetBuildingIds, ...sourceBuildingIds].map((buildingId) =>
          invalidateRentNavigatorBuilding(
            queryClient,
            buildingId,
            LOCATION_TYPE.ownLocation,
          ),
        ),
      );
    },
  });

  return { moveUnits: mutateAsync, isMoving: isPending };
};

export const useReamsServicePoints = ({
  propertyId,
  dataset,
}: {
  propertyId: number | null;
  dataset: string | null;
}) => {
  const params = { propertyId, dataset };
  const fetchServices = ({ signal }: { signal: any }) =>
    firebaseGetApi(reamsEndPoints.services, params, { signal });

  const enabled = Boolean(propertyId);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [servicesQueryKey, propertyId],
    queryFn: fetchServices,
    enabled,
  });

  return {
    data,
    isLoading: isLoading || isFetching,
  };
};

export const useReamsMunicipalitySummary = ({
  municipality,
  country,
}: {
  municipality: string | null;
  country: string | null;
}) => {
  const fetchSummary = ({ signal }: { signal: any }) =>
    firebaseGetApi(
      reamsEndPoints.municipalitySummary,
      { municipality, country },
      { signal },
    );

  const enabled = Boolean(municipality && country);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [municipalitySummary, municipality],
    queryFn: fetchSummary,
    enabled,
  });

  return {
    data,
    isLoading: isLoading || isFetching,
  };
};
export const useReamsDetailedProperties = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (datasets: any) =>
      firebaseGetApi(reamsEndPoints.detailedProperties, { datasets }),
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [trialUsageAndLimitationsKey],
        exact: true,
      });
    },
  });
  return { fetchDetailedProperties: mutateAsync, isLoading: isPending };
};

export const downloadRentNavigatorReport = async ({
  propertyId,
  locationName,
  dataset,
}: {
  propertyId: number;
  locationName: string;
  dataset: string;
}) => {
  const data = await firebaseGetApi(reamsEndPoints.report, {
    propertyId,
    dataset,
    responseType: 'blob',
  });

  const blob = new Blob([data], {
    type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  });

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${cleanLocationNameForFileDownload(locationName)}.pptx`;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const useRentNavigatorGenerateReport = (
  isFirstProperty: boolean = false,
) => {
  const queryClient = useQueryClient();

  const { mutateAsync: createRentNavigatorReport } = useMutation({
    mutationKey: [generateReport],
    mutationFn: ({
      propertyId,
      dataset,
    }: {
      propertyId: number;
      dataset: string;
    }) =>
      firebasePostApi(reamsEndPoints.report, {
        propertyId,
        dataset,
      }),
    onMutate: ({ propertyId, dataset }) => {
      isFirstProperty &&
        queryClient.setQueryData([rentNavigatorNewReport, propertyId], true);

      const property: IProperty | undefined = queryClient.getQueryData([
        reamsPropertyQueryKey,
        propertyId,
        dataset,
      ]);

      if (property) {
        queryClient.setQueriesData(
          { queryKey: [reamsPropertyQueryKey, propertyId, dataset] },
          { ...property, reportStatus: 'generating' },
        );
      }
    },
    onError: (error, { propertyId, dataset }) =>
      queryClient.invalidateQueries({
        queryKey: [reamsPropertyQueryKey, propertyId, dataset],
        exact: true,
      }),
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [trialUsageAndLimitationsKey],
          exact: true,
        }),
      ]),
  });

  return { createRentNavigatorReport };
};

export const useGeneratingReportIds = (): Set<number> => {
  const variables = useMutationState({
    filters: { mutationKey: [generateReport] },
    select: (mutation) => (mutation.state.variables as any)?.propertyId,
  });

  return new Set(variables.filter(Boolean) as number[]);
};

export const useRentNavigatorDataSourceDates = (country: string) => {
  const fetchDataSourceDates = ({ signal }: { signal: any }) =>
    firebaseGetApi(reamsEndPoints.dataSourcedDate, { country }, { signal });

  const enabled = Boolean(country);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [reamsDataSourcedDate, country],
    queryFn: fetchDataSourceDates,
    enabled,
  });

  return {
    sourcedDates: data?.data?.sourcedDates || {},
    isLoading: isLoading || isFetching,
  };
};

export const useRentNavigatorRentalCompanyNames = (query: string = '') => {
  const fetchCompanyNames = ({ signal }: { signal: any }) =>
    firebasePostApi(`${BASE_URL}/rental-company-names`, { query }, { signal });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [rentalCompanyNamesQueryKey, query],
    queryFn: fetchCompanyNames,
  });

  return {
    companyNames: (data?.companyNames as string[]) || [],
    isLoading: isLoading || isFetching,
  };
};

export const validatedAndReverseGeocode = async (
  lat: number,
  lng: number,
): Promise<string | null> => {
  try {
    const response = await firebasePostApi(
      reamsEndPoints.validateAndReverseGeocode,
      {
        lat,
        lng,
      },
    );

    return response;
  } catch {
    return null;
  }
};

export const usePortfolioConfiguration = () => {
  const { data, isLoading } = useQuery({
    queryKey: [saveConfigurationMutateKey],
    queryFn: ({ signal }) =>
      firebaseGetApi(reamsEndPoints.portfolioConfiguration, {}, { signal }),
  });

  return {
    excludedCompanies: (data?.excludedCompanies as string[]) ?? [],
    sharepointExcludedCompanies:
      (data?.sharepointExcludedCompanies as string[]) ?? [],
    excludeOwnCompany: (data?.excludeOwnCompany as boolean) ?? false,
    hasSharepoint: (data?.hasSharepoint as boolean) ?? false,
    updatedByEmail: (data?.updatedByEmail as string | null) ?? null,
    updatedAt: (data?.updatedAt as string | null) ?? null,
    isLoading,
  };
};

const recalculationStatusQueryKey = 'reams-recalculation-status';

export const useRecalculationStatus = (enabled: boolean = true) => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: [recalculationStatusQueryKey],
    queryFn: ({ signal }) =>
      firebaseGetApi(reamsEndPoints.recalculationStatus, {}, { signal }),
    enabled,
    refetchInterval: (query) =>
      enabled && (query.state.data as IRecalculationStatus)?.isRecalculating
        ? 5000
        : false,
  });

  const isRecalculating = (data?.isRecalculating as boolean) ?? false;
  const wasRecalculating = useRef(false);

  useEffect(() => {
    if (wasRecalculating.current && !isRecalculating) {
      queryClient.invalidateQueries({ queryKey: [reamsPropertyQueryKey] });
    }
    wasRecalculating.current = isRecalculating;
  }, [isRecalculating, queryClient]);

  return {
    isRecalculating,
    ownLocationsOutdatedCount: (data?.ownLocationsOutdatedCount as number) ?? 0,
    sharepointLocationsOutdatedCount:
      (data?.sharepointLocationsOutdatedCount as number) ?? 0,
    isLoading,
  };
};

export const useSavePortfolioConfiguration = () => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [saveConfigurationMutateKey],
    mutationFn: (config: IPortfolioConfig) =>
      firebasePostApi(reamsEndPoints.saveConfiguration, config),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [saveConfigurationMutateKey],
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: [recalculationStatusQueryKey],
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: [reamsPropertyQueryKey],
        }),
        queryClient.invalidateQueries({
          queryKey: [rentsQueryKey],
        }),
        queryClient.invalidateQueries({
          queryKey: [modelLineQueryKey],
        }),
      ]),
  });

  return { saveConfiguration: mutateAsync, isSaving: isPending };
};

export const useUpsertRentEstimateRating = ({
  propertyId,
  dataset,
}: {
  propertyId: number | null;
  dataset: string | null;
}) => {
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updateRatingApi = (status: string) =>
    firebasePostApi(reamsEndPoints.rentEstimateRating, {
      propertyId,
      dataset,
      status,
    });

  const { mutate, isPending } = useMutation({
    mutationKey: [updatePropertyRentRatingMutateKey],
    mutationFn: updateRatingApi,
  });

  const updateRating = useCallback(
    (status: string) => {
      if (!propertyId || !dataset) return;

      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        mutate(status);
      }, 400);
    },
    [propertyId, dataset, mutate],
  );

  return { updateRating, isPending };
};

const fetchAskingPricePredictionFor = async (
  queryClient: QueryClient,
  propertyId: number | null,
  dataset: string | null,
  signal?: AbortSignal,
): Promise<IAskingPricePrediction> => {
  const previous = queryClient.getQueryData<IAskingPricePrediction>([
    askingPricePredictionQueryKey,
    propertyId,
    dataset,
  ]);
  const result: IAskingPricePrediction = await firebasePostApi(
    askingPriceEndpoints.priceEstimation,
    { locationId: propertyId, dataset },
    { signal },
  );
  const isFirstReady =
    result?.status !== 'processing' &&
    (result?.isNewlyCalculated || previous?.status === 'processing');
  if (isFirstReady && result?.buildingId != null) {
    invalidateRentNavigatorBuilding(queryClient, result.buildingId, dataset);
  }
  return result;
};

export const useAskingPricePrediction = ({
  propertyId,
  dataset,
}: {
  propertyId: number | null;
  dataset: string | null;
}) => {
  const queryClient = useQueryClient();
  const fetchAskingPricePrediction = ({ signal }: { signal: any }) =>
    fetchAskingPricePredictionFor(queryClient, propertyId, dataset, signal);

  const isEnabled = Boolean(propertyId) && Boolean(dataset);

  const processingSinceRef = useRef<{ key: string; since: number } | null>(
    null,
  );
  const processingKey = `${propertyId}:${dataset}`;

  const { data, isLoading, isFetching, error, isError } =
    useQuery<IAskingPricePrediction>({
      queryKey: [askingPricePredictionQueryKey, propertyId, dataset],
      queryFn: fetchAskingPricePrediction,
      enabled: isEnabled,
      refetchOnWindowFocus: false,
      refetchInterval: (query) => {
        if (query.state.data?.status !== 'processing') {
          processingSinceRef.current = null;
          return false;
        }
        if (processingSinceRef.current?.key !== processingKey) {
          processingSinceRef.current = {
            key: processingKey,
            since: Date.now(),
          };
        }
        const elapsed = Date.now() - processingSinceRef.current.since;
        return elapsed < PRICE_PROCESSING_POLL_TIMEOUT_MS
          ? PRICE_PROCESSING_POLL_INTERVAL_MS
          : false;
      },
    });

  const isProcessing = data?.status === 'processing';
  const isProcessingTimedOut =
    isProcessing &&
    processingSinceRef.current?.key === processingKey &&
    Date.now() - processingSinceRef.current.since >=
      PRICE_PROCESSING_POLL_TIMEOUT_MS;

  const axiosError = error as AxiosError<{ code?: string }>;

  const isUnsupportedDwellingType =
    axiosError?.response?.data?.code === 'UNSUPPORTED_DWELLING_TYPE';

  return {
    prediction: data?.prediction,
    countryCode: data?.countryCode,
    isLoading:
      (isLoading || isFetching || (isProcessing && !isProcessingTimedOut)) &&
      isEnabled,
    isProcessingTimedOut,
    error: axiosError,
    isError,
    isUnsupportedDwellingType,
    isUnsupportedArea:
      axiosError?.response?.status === 404 && !isUnsupportedDwellingType,
  };
};

export interface IOwnershipFinancials {
  // the owner's own inputs, as currently stored
  hurdleRate: number | null;
  hurdleRateDefault: number | null;
  purchaseCost: number | null;
  maintenanceCharge: number | null;
  contractRentMonthly: number | null;
  // context the figures were computed against
  askingPrice: number | null;
  marketRentMonthly: number | null;
  numberOfSquareMeters: number | null;
  isEditable: boolean;
  // derived
  annualRent: number | null;
  rentalYieldPct: number | null;
  yieldOnCostPct: number | null;
  netYieldPct: number | null;
  sellVsHold: ISellVsHold | null;
}

export interface IOwnershipFinancialsPatch {
  hurdleRate?: number | null;
  purchaseCost?: number | null;
  maintenanceCharge?: number | null;
  discountValue?: number | null;
  agencyValue?: number | null;
  capitalGainsTax?: number | null;
  debtRepayment?: number | null;
  contractRentMonthly?: number | null;
}

export const useOwnershipFinancials = ({
  propertyId,
  dataset,
  askingPrice,
}: {
  propertyId: number | null;
  dataset: string | null;
  askingPrice: number | null | undefined;
}) => {
  const fetchOwnershipFinancials = ({ signal }: { signal: any }) =>
    firebaseGetApi(
      askingPriceEndpoints.ownershipFinancials,
      { locationId: propertyId, dataset, askingPrice },
      { signal },
    );

  // The sale price is the basis every yield here is measured against, so it
  // is part of the cache key, and the query waits for it rather than
  // fetching a set of figures that would all come back null.
  const isEnabled =
    Boolean(propertyId) && Boolean(dataset) && askingPrice != null;

  const { data, isLoading, isFetching, error, isError } =
    useQuery<IOwnershipFinancials>({
      queryKey: [ownershipFinancialsQueryKey, propertyId, dataset, askingPrice],
      queryFn: fetchOwnershipFinancials,
      enabled: isEnabled,
    });

  return {
    financials: data,
    isLoading: (isLoading || isFetching) && isEnabled,
    isFetching: isFetching && isEnabled,
    error: error as AxiosError,
    isError,
  };
};

export interface IYieldBenchmarkQuarter {
  year: number;
  quarter: number;
  label: string;
  grossYieldPct: number | null;
  medianRentPerSquareMeter: number | null;
  medianDebtFreePricePerSquareMeter: number | null;
  rentSampleSize: number;
  priceSampleSize: number;
  isThin: boolean;
}

export interface IYieldBenchmarkAsset {
  currentYieldPct: number | null;
  reLetYieldPct: number | null;
  contractRentMonthly: number | null;
  marketRentMonthly: number | null;
  rentPerSquareMeter: number | null;
  reLetRentPerSquareMeter: number | null;
  pricePerSquareMeter: number | null;
  askingPrice: number | null;
  numberOfSquareMeters: number | null;
}

export interface IYieldBenchmarks {
  areaCode: string | null;
  countryCode: string | null;
  numberOfRooms: number | null;
  quarterCount: number;
  quarters: IYieldBenchmarkQuarter[];
  thinQuarterLabels: string[];
  requiredYieldPct: number | null;
  asset: IYieldBenchmarkAsset;
  trend: {
    rentChangePct: number | null;
    priceChangePct: number | null;
  };
}

export const useYieldBenchmarks = ({
  propertyId,
  dataset,
  askingPrice,
  enabled = true,
}: {
  propertyId: number | null;
  dataset: string | null;
  askingPrice: number | null | undefined;
  enabled?: boolean;
}) => {
  const fetchYieldBenchmarks = ({ signal }: { signal: any }) =>
    firebaseGetApi(
      askingPriceEndpoints.yieldBenchmarks,
      { locationId: propertyId, dataset, askingPrice },
      { signal },
    );

  // The asset dots are placed against the sale price, so it is part of the
  // key -- fetching before the price estimate settles would draw the charts
  // once without the dots and then again with them. `enabled` lets the caller
  // hold the query until that estimate has resolved; it is resolving to *no*
  // price that is waited on, not a price existing, so an area with no
  // estimate still gets the market series.
  const isEnabled = Boolean(propertyId) && Boolean(dataset) && enabled;

  const { data, isLoading, isFetching, error, isError } =
    useQuery<IYieldBenchmarks>({
      queryKey: [yieldBenchmarksQueryKey, propertyId, dataset, askingPrice],
      queryFn: fetchYieldBenchmarks,
      enabled: isEnabled,
      refetchOnWindowFocus: false,
    });

  return {
    benchmarks: data,
    isLoading: (isLoading || isFetching) && isEnabled,
    error: error as AxiosError,
    isError,
  };
};

export const useUpdateOwnershipFinancials = () => {
  const queryClient = useQueryClient();

  const updateOwnershipFinancialsApi = ({
    propertyId,
    dataset,
    patch,
    askingPrice,
  }: {
    propertyId: number;
    dataset: string;
    patch: IOwnershipFinancialsPatch;
    askingPrice: number | null | undefined;
  }) =>
    firebasePostApi(askingPriceEndpoints.ownershipFinancials, {
      locationId: propertyId,
      dataset,
      askingPrice,
      ...patch,
    });

  const { mutateAsync, isPending } = useMutation({
    mutationKey: [ownershipFinancialsMutateKey],
    mutationFn: updateOwnershipFinancialsApi,
    onSuccess: (
      data: IOwnershipFinancials,
      { propertyId, dataset, askingPrice },
    ) => {
      queryClient.setQueryData(
        [ownershipFinancialsQueryKey, propertyId, dataset, askingPrice],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: [yieldBenchmarksQueryKey, propertyId, dataset],
      });
    },
  });

  return {
    updateOwnershipFinancials: (
      propertyId: number,
      dataset: string,
      patch: IOwnershipFinancialsPatch,
      askingPrice: number | null | undefined,
    ) => mutateAsync({ propertyId, dataset, patch, askingPrice }),
    isUpdating: isPending,
  };
};
